import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useRemoveAllCartDiscountsMutation } from 'renderer/features/payments-page/services/cart.endpoints';
import {
  useApplyCartDiscountMutation,
  useValidateDiscountMutation,
} from '../services/purchase.endpoints';

interface DiscountCodeProps {
  mode?: 'cart' | 'admin';
  initialCode?: string;
  onDiscountUpdate: (summary: any) => void; // Replace 'any' with 'CartPriceSummary | null' if CartPriceSummary is imported
  /** True when a promo is on the cart but an active sale beats it (choose-min). */
  saleBeatsAppliedDiscount?: boolean;
}

const SALE_BEATS_DISCOUNT_NOTE =
  'Your current sale price is better than this promo, so no additional discount is shown in the total.';

const DiscountCode: React.FC<DiscountCodeProps> = ({
  mode = 'cart',
  initialCode = '',
  onDiscountUpdate,
  saleBeatsAppliedDiscount = false,
}) => {
  const [discountCode, setDiscountCode] = useState(initialCode);
  const [feedback, setFeedback] = useState<{
    message: string;
    success: boolean;
  } | null>(null);

  const isAdmin = mode === 'admin';
  const [applyCartDiscount, { isLoading: isCartLoading }] =
    useApplyCartDiscountMutation();
  const [validateDiscount, { isLoading: isValidateLoading }] =
    useValidateDiscountMutation();
  const [removeAllCartDiscounts, { isLoading: isRemoving }] =
    useRemoveAllCartDiscountsMutation();

  const isLoading = isAdmin ? isValidateLoading : isCartLoading;

  // Cart may load after mount (Purchase refresh). Keep the field in sync with
  // whatever code is already stamped on cart lines — do not rely on useState(initial)
  // alone, and do not re-POST apply (discount is already on the cart).
  useEffect(() => {
    const code = (initialCode || '').trim();
    setDiscountCode(code);
    if (code) {
      setFeedback({
        message: 'Discount successfully applied.',
        success: true,
      });
    } else {
      setFeedback(null);
    }
  }, [initialCode]);

  const handleApply = async () => {
    if (!discountCode.trim()) return;

    try {
      if (isAdmin) {
        const response = await validateDiscount({
          discountCode: discountCode.trim(),
        }).unwrap();
        setFeedback({ message: response.message, success: response.success });
        onDiscountUpdate(response);
      } else {
        const response = await applyCartDiscount({
          discountCode: discountCode.trim(),
        }).unwrap();
        setFeedback({ message: response.message, success: response.success });
        onDiscountUpdate(response);
      }
    } catch (error: any) {
      setFeedback({
        message: error?.data?.message || 'Failed to apply discount',
        success: false,
      });
      onDiscountUpdate(null);
    }
  };

  const handleClear = async () => {
    if (isAdmin) {
      setDiscountCode('');
      setFeedback(null);
      onDiscountUpdate(null);
      return;
    }
    try {
      await removeAllCartDiscounts(null).unwrap();
      setDiscountCode('');
      setFeedback(null);
      onDiscountUpdate(null); // will trigger CartPage refetch
    } catch (err) {
      setFeedback({
        message: 'Failed to remove discount from cart.',
        success: false,
      });
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom sx={{ color: '#08c' }}>
        Have a Promo Code?
      </Typography>

      {feedback && (
        <Alert
          sx={{ mb: 2 }}
          severity={feedback.success ? 'success' : 'error'}
          action={
            <Tooltip title="Remove applied discount">
              <IconButton
                size="small"
                aria-label="Remove applied discount"
                onClick={handleClear}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          }
        >
          <Box>
            <Typography variant="body2" component="div">
              {feedback.message}
            </Typography>
            {feedback.success && saleBeatsAppliedDiscount && (
              <Typography
                variant="body2"
                component="div"
                sx={{ mt: 0.75, opacity: 0.95 }}
              >
                {SALE_BEATS_DISCOUNT_NOTE}
              </Typography>
            )}
          </Box>
        </Alert>
      )}

      <Box display="flex" alignItems="center" gap={1}>
        <TextField
          label="Promo Code"
          placeholder="Enter promo code"
          value={discountCode}
          onChange={(e) => setDiscountCode(e.target.value)}
          name="discountCode"
          variant="outlined"
          margin="none"
          size="small" // reduces height
          fullWidth
          InputProps={{
            style: { height: 40 }, // matches standard button height
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleApply}
          size="medium" // consistent height with default MUI buttons
          disabled={!discountCode.trim() || isLoading}
          sx={{ minWidth: '80px', height: 40 }} // height match and compact width
        >
          Apply
        </Button>
      </Box>
    </Box>
  );
};

export default DiscountCode;
